import { useState } from "react";
import toast from "react-hot-toast";
import {
  LegalAcceptanceAction,
  LegalDocument,
  LegalDocumentType,
  recordLegalAcceptance,
} from "../../services/legalDocument.service";
import Modal from "../../components/common/Modal";
import { LEGAL_CONTENT_CLASSES } from "../../utils/legalContent";
import { formatVersionDisplay } from "../../utils/semver";
import { Check, Eye, Scale, ShieldCheck } from "lucide-react";

const ACTION_LABELS: Record<LegalDocumentType, string> = {
  TERMS_AND_CONDITIONS: "I agree to the Terms & Conditions",
  PRIVACY_POLICY: "I acknowledge the Privacy Policy",
  MEDICAL_DISCLAIMER: "I acknowledge the Medical Disclaimer",
};

const TYPE_LABELS: Record<LegalDocumentType, string> = {
  TERMS_AND_CONDITIONS: "Terms & Conditions",
  PRIVACY_POLICY: "Privacy Policy",
  MEDICAL_DISCLAIMER: "Medical Disclaimer",
};

const ACTION_DEFAULT: LegalAcceptanceAction = "ACKNOWLEDGED";

const actionFor = (doc: LegalDocument): LegalAcceptanceAction =>
  doc.documentType === "TERMS_AND_CONDITIONS" ? "ACCEPTED" : ACTION_DEFAULT;

const extractError = (err: any): string => {
  const fallback = "Something went wrong. Please try again.";
  if (!err) return fallback;
  const data = err.response?.data;
  if (data && typeof data.message === "string") return data.message;
  return err.message || fallback;
};

interface LegalReviewProps {
  documents: LegalDocument[];
  onComplete?: () => void;
}

const LegalReview = ({ documents, onComplete }: LegalReviewProps) => {
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [viewDoc, setViewDoc] = useState<LegalDocument | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const allChecked = documents.length > 0 && documents.every((d) => checked[d.id]);

  const toggle = (id: string) =>
    setChecked((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleContinue = async () => {
    if (!allChecked || submitting) return;
    setSubmitting(true);
    try {
      await Promise.all(
        documents.map((doc) =>
          recordLegalAcceptance({
            legalDocumentId: doc.id,
            action: actionFor(doc),
          })
        )
      );
      toast.success("Legal documents accepted");
      onComplete?.();
    } catch (err: any) {
      toast.error(extractError(err));
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5">
          <div className="flex items-center gap-3">
            <span className="relative flex h-10 w-10">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-400 opacity-30"></span>
              <span className="relative inline-flex h-10 w-10 rounded-full bg-primary-600 items-center justify-center">
                <Scale size={18} className="text-white" />
              </span>
            </span>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-gray-900 tracking-tight">
                Legal Review
              </h1>
              <p className="text-sm text-gray-500">
                Required legal documents need your review before you continue.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-6 space-y-4">
        {documents.map((doc) => {
          const label = ACTION_LABELS[doc.documentType] || `I acknowledge ${doc.title}`;
          const isChecked = !!checked[doc.id];
          return (
            <div
              key={doc.id}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 space-y-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center text-primary-600 shrink-0">
                    <ShieldCheck size={19} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {TYPE_LABELS[doc.documentType] || doc.title}
                    </p>
                    <p className="text-xs text-gray-400">
                      Version {formatVersionDisplay(doc.version)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setViewDoc(doc)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-600 border border-gray-200 hover:bg-gray-50 hover:text-gray-800 transition-colors shrink-0"
                >
                  <Eye size={14} />
                  View
                </button>
              </div>

              <label className="flex items-start gap-3 cursor-pointer select-none">
                <span
                  className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
                    isChecked
                      ? "bg-primary-600 border-primary-600 text-white"
                      : "border-gray-300 bg-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={isChecked}
                    onChange={() => toggle(doc.id)}
                    disabled={submitting}
                  />
                  {isChecked && <Check size={13} strokeWidth={3} />}
                </span>
                <span className="text-sm text-gray-700">{label}</span>
              </label>
            </div>
          );
        })}

        {/* Continue */}
        <div className="pt-2">
          <button
            type="button"
            onClick={handleContinue}
            disabled={!allChecked || submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            {submitting ? "Submitting..." : "Continue"}
          </button>
          <p className="text-center text-xs text-gray-400 mt-3">
            You must review and accept all required documents to continue.
          </p>
        </div>
      </div>

      {/* View document modal */}
      <Modal
        isOpen={viewDoc !== null}
        title={viewDoc?.title || "Legal Document"}
        subtitle={
          viewDoc
            ? `${TYPE_LABELS[viewDoc.documentType] || viewDoc.documentType} · Version ${formatVersionDisplay(viewDoc.version)}`
            : undefined
        }
        onClose={() => setViewDoc(null)}
        className="max-w-3xl"
      >
        {viewDoc && (
          <div className="p-6">
            <div
              className={LEGAL_CONTENT_CLASSES}
              dangerouslySetInnerHTML={{ __html: viewDoc.content }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LegalReview;